import React, { useEffect, useState } from 'react';
import { FullWidthContainer } from '@vp/react-vp-ui-library';
import { useDispatch, useMappedState } from 'redux-react-hook';

import UploadArea from './components/UploadArea';
import SpecificationArea from './components/SpecificationArea';
import Steps from './components/Steps';
import { getAllUniqueProductShapes } from './services/products';
import websiteContext from './websiteContext';
import {
  createSetMPIdAction,
  createSetProjectIdAction,
  createSetLoadableProductShapesAction,
  createSetBaseOfferAction,
  createSetPricingContextAction,
  createSetLoadableUpsellPricesBySKUAction,
  createSetLoadableProductSpecsBySKUAction,
  createSetLoadableProjectAction,
} from './state/actionCreators';
import {
  getPricingContext,
  batchGetUpsellPricePresentationBySKU,
  getBaseOfferBySKUs,
} from './services/pricing';
import { getProject } from './services/project';
import { State } from './state/reducer';
import { getProductSpecsBySKU, ProductSpecs } from './services/specs';
import PreloaderGraphic from './components/PreloaderGraphic';
import { ErrorPage } from './components/ErrorPage';
import { logError } from './utilities/logger';
import { getQueryParameter } from './utilities/url';

const mapState = (state: State) => ({
  mpId: state.mpId,
  projectId: state.projectId,
  project: state.loadableProject.data,
  productShapes: state.loadableProductShapes.data,
  baseOffer: state.loadableBaseOffer.data,
  pricingContext: state.loadablePricingContext.data,
  upsellPriceBySKU: state.loadableUpsellPriceBySKU.data,
  productSpecsBySKU: state.loadableProductSpecsBySKU.data,

  projectError: state.loadableProject.error,
  productShapesError: state.loadableProductShapes.error,
  baseOfferError: state.loadableBaseOffer.error,
  pricingContextError: state.loadablePricingContext.error,
  upsellPriceBySKUError: state.loadableUpsellPriceBySKU.error,
  productSpecsBySKUError: state.loadableProductSpecsBySKU.error,

  projectLoading: state.loadableProject.loading,
  productShapesLoading: state.loadableProductShapes.loading,
  baseOfferLoading: state.loadableBaseOffer.loading,
  pricingContextLoading: state.loadablePricingContext.loading,
  upsellPriceBySKULoading: state.loadableUpsellPriceBySKU.loading,
  productSpecsBySKULoading: state.loadableProductSpecsBySKU.loading,
});

function App() {
  const dispatch = useDispatch();

  const {
    mpId,
    projectId,
    project,
    baseOffer,
    pricingContext,
    productShapes,

    projectError,
    baseOfferError,
    pricingContextError,
    productSpecsBySKUError,
    productShapesError,
    upsellPriceBySKUError,

    projectLoading,
    baseOfferLoading,
    pricingContextLoading,
    productSpecsBySKULoading,
    productShapesLoading,
    upsellPriceBySKULoading,
  } = useMappedState(mapState);

  // keep track of when we have no identifier
  const [noIdentifier, setNoIdentifier] = useState(false);

  /*
   * Get mp id or project id from URL
   */

  useEffect(() => {
    const merchProductId = getQueryParameter('mp_id');
    const projectId = getQueryParameter('project_id');

    if (projectId) {
      dispatch(createSetProjectIdAction(projectId));
    } else if (merchProductId) {
      dispatch(createSetMPIdAction(merchProductId));
    } else {
      const error = new Error('No mp_id or project_id provided');
      logError(error);

      setNoIdentifier(true);
    }
  }, [dispatch]);

  /*
   * Get project if we have a projectId
   */

  useEffect(() => {
    if (projectId === undefined) {
      return;
    }

    getProject(projectId)
      .then(project => {
        dispatch(
          createSetLoadableProjectAction({
            loading: false,
            data: project,
            error: undefined,
          }),
        );

        // also set the mpId
        dispatch(createSetMPIdAction(project.merchandisingProductId));
      })
      .catch(error => {
        logError(error);

        dispatch(
          createSetLoadableProjectAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch, projectId]);

  /*
   * Get all unique SKU shapes
   */

  useEffect(() => {
    // if we're still loading resources, skip this effect
    if (projectLoading) {
      return;
    }

    // if we failed to get an mpId AND failed to get a project, skip this effect
    if (projectError) {
      return;
    }

    if (mpId === undefined) {
      return;
    }

    const language = websiteContext.getLanguage();

    getAllUniqueProductShapes(mpId, language, { project })
      .then(productShapes => {
        dispatch(
          createSetLoadableProductShapesAction({
            loading: false,
            data: productShapes,
            error: undefined,
          }),
        );
      })
      .catch(error => {
        logError(error);

        dispatch(
          createSetLoadableProductShapesAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch, mpId, project, projectLoading, projectError]);

  /*
   * Get pricing context
   */

  useEffect(() => {
    const culture = websiteContext.getLanguage();
    const vpLocale = websiteContext.getVPLocale();
    const couponCode = websiteContext.getCouponFriendlyName();
    const includeTax = websiteContext.getVATPreference();

    getPricingContext(culture, vpLocale, { couponCode, includeTax })
      .then(pricingContext => {
        dispatch(
          createSetPricingContextAction({
            loading: false,
            data: pricingContext,
            error: undefined,
          }),
        );
      })
      .catch(error => {
        logError(error);

        dispatch(
          createSetPricingContextAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch]);

  /*
   * Get base offer (e.g. 100 for $16.00)
   */

  useEffect(() => {
    if (pricingContext === undefined) {
      return;
    }

    if (projectLoading) {
      return;
    }

    if (productShapes === undefined) {
      return;
    }

    if (mpId === undefined) {
      return;
    }

    let quantity: undefined | number;
    if (
      project !== undefined &&
      project.quantitiesByVariantAttributeChoices !== undefined &&
      project.quantitiesByVariantAttributeChoices.length > 0 &&
      project.quantitiesByVariantAttributeChoices[0].quantity !== undefined
    ) {
      quantity = project.quantitiesByVariantAttributeChoices[0].quantity;
    }

    getBaseOfferBySKUs(productShapes.map(productShape => productShape.sku), pricingContext, {
      quantity,
    })
      .then(offer => {
        dispatch(
          createSetBaseOfferAction({
            loading: false,
            data: offer,
            error: undefined,
          }),
        );
      })
      .catch(error => {
        logError(error);

        dispatch(
          createSetBaseOfferAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch, mpId, pricingContext, productShapes, project, projectLoading]);

  /*
   * Get product specs for all skus
   */

  useEffect(() => {
    if (productShapes === undefined) {
      return;
    }

    const culture = websiteContext.getLanguage();

    const getProductSpecsBySKUPromises = productShapes.map(productShape => {
      return getProductSpecsBySKU(productShape.sku, culture);
    });

    Promise.all(getProductSpecsBySKUPromises)
      .then(results => {
        // Promise.all guarantees that the order of the results will match the order of the Promises passed to Promise.all
        // since we mapped the Promises from the skus array, the order of results will match with the order of the skus array as well

        if (productShapes.length !== results.length) {
          throw new Error(
            'The number of ProductSpecsBySKU results does not match the number of SKUs',
          );
        }

        const productSpecsBySKU = productShapes.reduce<{ [sku: string]: ProductSpecs }>(
          (acc, productShape, index) => {
            acc[productShape.sku] = results[index];
            return acc;
          },
          {},
        );

        dispatch(
          createSetLoadableProductSpecsBySKUAction({
            loading: false,
            data: productSpecsBySKU,
            error: undefined,
          }),
        );
      })
      .catch((error: Error) => {
        logError(error);

        dispatch(
          createSetLoadableProductSpecsBySKUAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch, productShapes]);

  /*
   * Get upsell price for all SKUs
   */

  useEffect(() => {
    if (productShapes === undefined) {
      return;
    }

    if (pricingContext === undefined) {
      return;
    }

    if (baseOffer === undefined) {
      return;
    }

    batchGetUpsellPricePresentationBySKU(
      productShapes.map(productShape => productShape.sku),
      baseOffer.quantity,
      pricingContext,
    ) // f typescript
      .then((pricesBySKU: { [sku: string]: string }) => {
        dispatch(
          createSetLoadableUpsellPricesBySKUAction({
            loading: false,
            data: pricesBySKU,
            error: undefined,
          }),
        );
      })
      .catch(error => {
        logError(error);

        dispatch(
          createSetLoadableUpsellPricesBySKUAction({
            loading: false,
            data: undefined,
            error: error.message,
          }),
        );
      });
  }, [dispatch, pricingContext, baseOffer, productShapes]);

  if (
    noIdentifier ||
    baseOfferError ||
    productShapesError ||
    upsellPriceBySKUError ||
    pricingContextError ||
    productSpecsBySKUError
  ) {
    return <ErrorPage />;
  }

  return (
    <FullWidthContainer capped>
      {baseOfferLoading ||
      productShapesLoading ||
      upsellPriceBySKULoading ||
      pricingContextLoading ||
      productSpecsBySKULoading ? (
        <PreloaderGraphic variant="extra-large" centered={true} />
      ) : (
        <>
          <Steps />
          <UploadArea />
          <SpecificationArea />
        </>
      )}
    </FullWidthContainer>
  );
}

export default App;
